import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Field } from 'react-final-form';
import clsx from 'clsx';

import DocsButton from '../../../../scout-new/components/generation-section/components/docs-button/DocsButton';
import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import WithError from '../../../../components/with-error/WithError';
import { Input } from '../../../../components/common-inputs';
import { BUTTON_STYLES } from '../../../../components/common-button/CommonButton';
import Icon from '../../../../icons/Icon';
import { ICONS } from '../../../../icons';
import RepliesForm from '../replies-form/RepliesForm';
import TooltipButton from '../../../../components/tooltip-button/TooltipButton';
import useIsMessagesGenerating from '../../../hooks/useIsMessagesGenerating';
import { ID8_TABS } from '../../../../constants';

import scss from './GenerationForm.module.scss';

function GenerationForm({ onSubmit, onReply, onResetSession, onFileUpload }) {
  const { t } = useTranslation('id8');
  const dispatch = useDispatch();
  const { id } = useParams();

  const isFormLoading = useSelector((state) => state.id8.isFormLoading);
  const isResetting = useSelector((state) => state.id8.isResetting);
  const isFileUploading = useSelector((state) => state.scout.isFileUploading);
  const id8Tab = useSelector((state) => state.id8.id8Tab);
  const isGenerating = useIsMessagesGenerating();

  const disableTextFields = isFormLoading || isResetting || isGenerating || id8Tab !== ID8_TABS.IDEAS;
  const disableButtons = isFormLoading || isGenerating || id8Tab !== ID8_TABS.IDEAS;
  const disableDocButton = isFormLoading || isResetting || isFileUploading || isGenerating || id8Tab !== ID8_TABS.IDEAS;

  const handleResetSession = () => {
    dispatch(onResetSession(id));
  };

  return (
    <form className={scss.form} id='id8_generation_form' onSubmit={onSubmit}>
      <div className={scss.topBlock}>
        <Field name='url'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label='URL' isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input
                  size='sm'
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onBlur={input.onBlur}
                  disabled={disableTextFields}
                  isError={meta.touched && meta.error}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='focusOn'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label={t('topicIdeas')} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input
                  size='sm'
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onBlur={input.onBlur}
                  disabled={disableTextFields}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='byPass'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label={t('bypass')} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input
                  size='sm'
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onBlur={input.onBlur}
                  disabled={disableTextFields}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <DocsButton onFileUpload={onFileUpload} disabled={disableDocButton} isLoading={isFileUploading} />

        <TooltipButton
          styleColor={BUTTON_STYLES.SECONDARY}
          onClick={handleResetSession}
          maxWidth={140}
          disabled={disableButtons}
          tooltip={isGenerating && t('messagesGenerating')}>
          {t('resetSession')}
          <Icon icon={ICONS.Reset} className={clsx(isResetting && scss.spinAnimation)} />
        </TooltipButton>
      </div>

      <RepliesForm onReply={onReply} />
    </form>
  );
}

export default GenerationForm;
